import { Link } from "react-router-dom";
import { ShoppingBag, Wrench, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

const TYPES = [
  { label: "Urbano", to: "/buscar?tipo=urbano" },
  { label: "Plegable", to: "/buscar?tipo=plegable" },
  { label: "Todoterreno", to: "/buscar?tipo=todoterreno" },
  { label: "Largo alcance", to: "/buscar?tipo=largo-alcance" },
  { label: "Alta velocidad", to: "/buscar?tipo=alta-velocidad" },
  { label: "Doble motor", to: "/buscar?tipo=doble-motor" },
  { label: "Asiento", to: "/buscar?tipo=asiento" },
  { label: "Infantil", to: "/buscar?tipo=infantil" },
  { label: "Homologados DGT", to: "/buscar?dgt=1" },
];

const BRANDS = [
  "Xiaomi",
  "Segway",
  "Cecotec",
  "SmartGyro",
  "Dualtron",
  "Kaabo",
  "NIU",
  "Hiboy",
  "Inokim",
  "Vsett",
];

const PARTS = [
  { label: "Baterías", to: "/buscar?categoria=baterias" },
  { label: "Neumáticos y cámaras", to: "/buscar?categoria=neumaticos" },
  { label: "Frenos y pastillas", to: "/buscar?categoria=frenos" },
  { label: "Motores", to: "/buscar?categoria=motores" },
  { label: "Controladoras", to: "/buscar?categoria=controladoras" },
  { label: "Pantallas", to: "/buscar?categoria=pantallas" },
];

const ACCESSORIES = [
  { label: "Cascos", to: "/buscar?categoria=cascos" },
  { label: "Candados", to: "/buscar?categoria=candados" },
  { label: "Luces", to: "/buscar?categoria=luces" },
  { label: "Soportes móvil", to: "/buscar?categoria=soportes" },
  { label: "Alforjas y bolsas", to: "/buscar?categoria=bolsas" },
  { label: "Cargadores", to: "/buscar?categoria=cargadores" },
];

const POWER = [
  { label: "Hasta 250 W", to: "/buscar?potencia=0-250" },
  { label: "250 - 500 W", to: "/buscar?potencia=250-500" },
  { label: "500 - 1000 W", to: "/buscar?potencia=500-1000" },
  { label: "Más de 1000 W", to: "/buscar?potencia=1000+" },
];

interface Props {
  onNavigate?: () => void;
}

const ComprarMegaMenu = ({ onNavigate }: Props) => {
  const linkCls = "block rounded-md px-2 py-1.5 text-sm text-foreground/75 transition-colors hover:bg-muted hover:text-foreground";
  const sectionTitle = "mb-3 border-b border-border pb-1.5 font-display text-sm font-semibold text-foreground";

  const handle = () => onNavigate?.();

  return (
    <div className="w-[760px] p-5">
      {/* Top chips */}
      <div className="mb-5 flex flex-wrap gap-2">
        <Link
          to="/buscar?condicion=usado"
          onClick={handle}
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-transform hover:scale-[1.03]"
        >
          <ShoppingBag className="h-3.5 w-3.5" /> Patinetes ocasión
        </Link>
        <Link
          to="/buscar?condicion=nuevo"
          onClick={handle}
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-transform hover:scale-[1.03]"
        >
          <Sparkles className="h-3.5 w-3.5" /> Patinetes nuevos
        </Link>
        <Link
          to="/buscar?outlet=1"
          onClick={handle}
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition-transform hover:scale-[1.03]"
        >
          <Zap className="h-3.5 w-3.5" /> Outlet
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Tipos */}
        <div>
          <h4 className={sectionTitle}>Tipos</h4>
          <div className="flex flex-col">
            {TYPES.map((t) => (
              <Link key={t.to} to={t.to} onClick={handle} className={linkCls}>
                {t.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Marcas populares */}
        <div>
          <h4 className={sectionTitle}>Marcas Populares</h4>
          <div className="flex flex-col">
            {BRANDS.map((b) => (
              <Link
                key={b}
                to={`/buscar?marca=${encodeURIComponent(b)}`}
                onClick={handle}
                className={linkCls}
              >
                {b}
              </Link>
            ))}
          </div>
        </div>

        {/* Recambios + Accesorios + Potencia */}
        <div className="space-y-5">
          <div>
            <h4 className={cn(sectionTitle, "flex items-center gap-1.5")}>
              <Wrench className="h-3.5 w-3.5" /> Recambios
            </h4>
            <div className="flex flex-col">
              {PARTS.map((p) => (
                <Link key={p.to} to={p.to} onClick={handle} className={linkCls}>
                  {p.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className={sectionTitle}>Accesorios</h4>
            <div className="flex flex-col">
              {ACCESSORIES.map((a) => (
                <Link key={a.to} to={a.to} onClick={handle} className={linkCls}>
                  {a.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Power footer */}
      <div className="mt-5 border-t border-border pt-4">
        <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Por potencia
        </h4>
        <div className="flex flex-wrap gap-2">
          {POWER.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              onClick={handle}
              className="rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-foreground/80 transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
            >
              {p.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComprarMegaMenu;
